'use client';

import styles from './EnterpriseMockTable.module.css';
import {
  Column,
  Input,
  Dropdown,
  Text,
  Select,
  Flex,
  CardTable,
  CellContainer,
  CellActions,
  ButtonGroup,
  Button,
} from '@/_components';
import type { EnterpriseMockTableProps } from './EnterpriseMockTable.types';
import type { FC } from 'react';

interface UserTableItem {
  name: string;
  action: string;
  time: string;
}

interface ApiKeyTableItem {
  key: string;
  issued: string;
  permission: string;
}

interface ApiUserTableItem {
  email: string;
  role: string;
  lastActive: string;
}

interface FirmPermissionTableItem {
  user: string;
  clientAccess: string;
  documentsAccess: string;
}

interface TaxReturnDeliveryTableItem {
  client: string;
  returnType: string;
  status: string;
}

const TABLE_USERS_COLUMNS = ['Name', 'Action', 'Time'] as const;
const TABLE_USERS_ITEMS: UserTableItem[] = [
  { name: 'John Smith', action: 'Accessed client portal', time: '2 days ago' },
  {
    name: 'Sarah Johnson',
    action: 'Downloaded tax documents',
    time: '3 days ago',
  },
  {
    name: 'Michael Chen',
    action: 'Submitted questionnaire',
    time: '5 days ago',
  },
  {
    name: 'Emily Davis',
    action: 'Viewed financial report',
    time: '1 week ago',
  },
  {
    name: 'David Martinez',
    action: 'Updated profile information',
    time: '1 week ago',
  },
  {
    name: 'Lisa Anderson',
    action: 'Requested document upload',
    time: '2 weeks ago',
  },
  {
    name: 'Robert Wilson',
    action: 'Completed tax return review',
    time: '2 weeks ago',
  },
  {
    name: 'Jennifer Taylor',
    action: 'Signed engagement letter',
    time: '3 weeks ago',
  },
];

const TABLE_API_KEYS_COLUMNS = ['Key', 'Issued', 'Permission'] as const;
const TABLE_API_KEYS_ITEMS: ApiKeyTableItem[] = [
  {
    key: 'sb_live_51Jf...xYz2',
    issued: '2 days ago',
    permission: 'Read/Write',
  },
  { key: 'sb_live_39Ka...mN41', issued: '1 week ago', permission: 'Read Only' },
  {
    key: 'sb_live_72Bc...pQ89',
    issued: '2 weeks ago',
    permission: 'Read/Write',
  },
  {
    key: 'sb_live_18Td...vR56',
    issued: '3 weeks ago',
    permission: 'Read Only',
  },
];

const TABLE_API_USERS_COLUMNS = ['Email', 'Role', 'Last active'] as const;
const TABLE_API_USERS_ITEMS: ApiUserTableItem[] = [
  {
    email: 'john.doe@acctfirm.com',
    role: 'Administrator',
    lastActive: '2 hours ago',
  },
  {
    email: 'sarah.miller@acctfirm.com',
    role: 'Developer',
    lastActive: '1 day ago',
  },
  {
    email: 'mike.johnson@acctfirm.com',
    role: 'Read Only',
    lastActive: '3 days ago',
  },
  {
    email: 'emma.williams@acctfirm.com',
    role: 'Developer',
    lastActive: '5 days ago',
  },
  {
    email: 'chris.brown@acctfirm.com',
    role: 'Administrator',
    lastActive: '1 week ago',
  },
  {
    email: 'lisa.garcia@acctfirm.com',
    role: 'Read Only',
    lastActive: '2 weeks ago',
  },
];

const TABLE_FIRM_PERMISSIONS_COLUMNS = [
  'User',
  'Client access',
  'Documents access',
] as const;
const TABLE_FIRM_PERMISSIONS_ITEMS: FirmPermissionTableItem[] = [
  {
    user: 'Alex Thompson',
    clientAccess: 'Smith Family Trust',
    documentsAccess: 'View & Edit',
  },
  {
    user: 'Rachel Green',
    clientAccess: 'Johnson Corp LLC',
    documentsAccess: 'View Only',
  },
  {
    user: 'Marcus Lee',
    clientAccess: 'Davis Enterprises',
    documentsAccess: 'View & Edit',
  },
  {
    user: 'Nina Patel',
    clientAccess: 'Anderson Holdings',
    documentsAccess: 'View Only',
  },
  {
    user: 'Tom Richardson',
    clientAccess: 'Martinez Family',
    documentsAccess: 'View & Edit',
  },
  {
    user: 'Kelly Walsh',
    clientAccess: 'Wilson Partnership',
    documentsAccess: 'View & Edit',
  },
  {
    user: 'Brandon Kim',
    clientAccess: 'Taylor Industries',
    documentsAccess: 'View Only',
  },
];

const TABLE_TAX_RETURN_DELIVERY_COLUMNS = [
  'Client',
  'Return type',
  'Status',
] as const;
const TABLE_TAX_RETURN_DELIVERY_ITEMS: TaxReturnDeliveryTableItem[] = [
  {
    client: 'Thompson Consulting',
    returnType: '1040 Individual',
    status: 'Delivered',
  },
  { client: 'Garcia LLC', returnType: '1120 Corporate', status: 'Pending' },
  {
    client: 'Williams Family Trust',
    returnType: '1041 Estate/Trust',
    status: 'Delivered',
  },
  {
    client: 'Anderson & Associates',
    returnType: '1065 Partnership',
    status: 'In Progress',
  },
  {
    client: 'Martinez Holdings',
    returnType: '1120S S-Corp',
    status: 'Delivered',
  },
  {
    client: 'Chen Enterprises',
    returnType: '1040 Individual',
    status: 'Pending',
  },
  {
    client: 'Roberts Family',
    returnType: '1040 Individual',
    status: 'Delivered',
  },
];

const TABLE_CONFIG = {
  users: {
    columns: TABLE_USERS_COLUMNS,
    items: TABLE_USERS_ITEMS,
  },
  'api-keys': {
    columns: TABLE_API_KEYS_COLUMNS,
    items: TABLE_API_KEYS_ITEMS,
  },
  'api-users': {
    columns: TABLE_API_USERS_COLUMNS,
    items: TABLE_API_USERS_ITEMS,
  },
  'granular-firm-permissions': {
    columns: TABLE_FIRM_PERMISSIONS_COLUMNS,
    items: TABLE_FIRM_PERMISSIONS_ITEMS,
  },
  'tax-return-delivery': {
    columns: TABLE_TAX_RETURN_DELIVERY_COLUMNS,
    items: TABLE_TAX_RETURN_DELIVERY_ITEMS,
  },
} as const;

export const EnterpriseMockTable: FC<EnterpriseMockTableProps> = ({
  type = 'users',
}) => {
  const config = TABLE_CONFIG[type];
  const columns = config.columns;
  const items = config.items;

  return (
    <Flex
      isColumn={true}
      gap={12}
      className={styles.enterpriseMockTable}
      padding={6}
    >
      <Flex isYCentered={true} gap={8} isWrapping={true}>
        <Column width={320}>
          <Input
            value=""
            isDisabled={true}
            onChange={() => null}
            label="Search"
            placeholder="Search by action or name"
            isLabelHidden={true}
          />
        </Column>
        <Select
          isDisabled={true}
          items={[
            { value: 'mock', name: 'Mock' },
            { value: 'selected', name: 'Authentication requests' },
          ]}
          isLabelHidden={true}
          selectedValue="selected"
          label="Select"
          onChange={() => null}
        />
      </Flex>
      <CardTable isInline={true} isLoading={false}>
        <table className="sorabanTable">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column}
                  className={index === 0 ? 'extraPadding' : undefined}
                >
                  <CellContainer
                    isHeader={true}
                    text={column}
                    width={
                      index === 0
                        ? 250
                        : index === 1
                          ? 300
                          : index === 2
                            ? 150
                            : undefined
                    }
                  />
                </th>
              ))}
              <th className="sorabanTable__fixedColumn" />
            </tr>
          </thead>
          <tbody>
            {type === 'users' &&
              (items as UserTableItem[]).map((item, index) => (
                <tr key={index}>
                  <td className="extraPadding">
                    <CellContainer>
                      <Text type="small" fontWeight="medium" lineClamp={2}>
                        {item.name}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small">{item.action}</Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small" color="secondary">
                        {item.time}
                      </Text>
                    </CellContainer>
                  </td>
                  <td className="sorabanTable__fixedColumn">
                    <CellActions>
                      <ButtonGroup>
                        <Dropdown items={[]} placement="left" isDisabled={true}>
                          <Button
                            value="Actions"
                            isDisabled={true}
                            iconLeft="dots-three"
                            intent="ghost"
                            isIconOnly={true}
                            size="small"
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </CellActions>
                  </td>
                </tr>
              ))}
            {type === 'api-keys' &&
              (items as ApiKeyTableItem[]).map((item, index) => (
                <tr key={index}>
                  <td className="extraPadding">
                    <CellContainer>
                      <Text type="small" fontWeight="medium" lineClamp={2}>
                        {item.key}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small" color="secondary">
                        {item.issued}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small">{item.permission}</Text>
                    </CellContainer>
                  </td>
                  <td className="sorabanTable__fixedColumn">
                    <CellActions>
                      <ButtonGroup>
                        <Dropdown items={[]} placement="left" isDisabled={true}>
                          <Button
                            value="Actions"
                            isDisabled={true}
                            iconLeft="dots-three"
                            intent="ghost"
                            isIconOnly={true}
                            size="small"
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </CellActions>
                  </td>
                </tr>
              ))}
            {type === 'api-users' &&
              (items as ApiUserTableItem[]).map((item, index) => (
                <tr key={index}>
                  <td className="extraPadding">
                    <CellContainer>
                      <Text type="small" fontWeight="medium" lineClamp={2}>
                        {item.email}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small">{item.role}</Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small" color="secondary">
                        {item.lastActive}
                      </Text>
                    </CellContainer>
                  </td>
                  <td className="sorabanTable__fixedColumn">
                    <CellActions>
                      <ButtonGroup>
                        <Dropdown items={[]} placement="left" isDisabled={true}>
                          <Button
                            value="Actions"
                            isDisabled={true}
                            iconLeft="dots-three"
                            intent="ghost"
                            isIconOnly={true}
                            size="small"
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </CellActions>
                  </td>
                </tr>
              ))}
            {type === 'granular-firm-permissions' &&
              (items as FirmPermissionTableItem[]).map((item, index) => (
                <tr key={index}>
                  <td className="extraPadding">
                    <CellContainer>
                      <Text type="small" fontWeight="medium" lineClamp={2}>
                        {item.user}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small">{item.clientAccess}</Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small" color="secondary">
                        {item.documentsAccess}
                      </Text>
                    </CellContainer>
                  </td>
                  <td className="sorabanTable__fixedColumn">
                    <CellActions>
                      <ButtonGroup>
                        <Dropdown items={[]} placement="left" isDisabled={true}>
                          <Button
                            value="Actions"
                            isDisabled={true}
                            iconLeft="dots-three"
                            intent="ghost"
                            isIconOnly={true}
                            size="small"
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </CellActions>
                  </td>
                </tr>
              ))}
            {type === 'tax-return-delivery' &&
              (items as TaxReturnDeliveryTableItem[]).map((item, index) => (
                <tr key={index}>
                  <td className="extraPadding">
                    <CellContainer>
                      <Text type="small" fontWeight="medium" lineClamp={2}>
                        {item.client}
                      </Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small">{item.returnType}</Text>
                    </CellContainer>
                  </td>
                  <td>
                    <CellContainer>
                      <Text type="small" color="secondary">
                        {item.status}
                      </Text>
                    </CellContainer>
                  </td>
                  <td className="sorabanTable__fixedColumn">
                    <CellActions>
                      <ButtonGroup>
                        <Dropdown items={[]} placement="left" isDisabled={true}>
                          <Button
                            value="Actions"
                            isDisabled={true}
                            iconLeft="dots-three"
                            intent="ghost"
                            isIconOnly={true}
                            size="small"
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </CellActions>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardTable>
    </Flex>
  );
};
